import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Highlighter, Info, PenLine, X as XIcon } from 'lucide-react';
import { buildLineItemModel, isSampleColumn, lineCellValue, evidenceEntries } from './lineItemModel';
import { lineLevelFieldValue } from './itemFields';
import LineItemAnnotationViewer from './LineItemAnnotationViewer';
import { buildCitationPanels } from './annotationPanels';
import {
  lineColumnVisible, lineColumnsStorageKey, loadLineColumnChoices, sourceText,
} from '../../features/control-testing/report/fields.ts';

/**
 * One row per sample and line item. The sample's own columns (plan fields) repeat on every row so each row
 * reads on its own. Attributes are not a column: each row carries the result of every attribute mapped to its
 * sample. The evidence cell lists only the files mapped to the row's sample (see lineItemModel).
 */

// A statement can have thousands of lines: render them in pages so the table stays responsive.
export const PAGE_SIZE = 200;

const MARK = { passed: '✓', failed: '✗', warning: '!', skipped: '–', incomplete: '↻' };
const CHIP = {
  passed: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  warning: 'bg-amber-50 text-amber-700',
  incomplete: 'bg-gray-100 text-gray-600 border border-dashed border-gray-300',
};
const STATUS_TEXT = { incomplete: 'not completed — re-run the test' };
const TONE = { ok: 'text-green-700', warn: 'text-amber-600', muted: 'text-gray-400' };

const th = 'px-2 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200 text-left align-bottom';
const td = 'px-2 py-1.5 text-[11px] text-gray-700 align-top border-b border-gray-100';

// Where a column's values were read, for the info icon on its header: "Statement.pdf (p. 1); Invoices.pdf (p. 2, 5)".
// Shared with the working paper (features/control-testing/report/fields.ts).
export { sourceText };

function useColumnState(runId) {
  const key = runId ? lineColumnsStorageKey(runId) : null;
  const [overrides, setOverrides] = useState(() => loadLineColumnChoices(runId));
  const set = (id, visible) => setOverrides((prev) => {
    const next = { ...prev, [id]: visible };
    if (key) { try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage may be blocked */ } }
    return next;
  });
  const isVisible = (col) => lineColumnVisible(overrides, col);
  return { isVisible, set };
}

function ColumnsMenu({ cols, isVisible, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  if (cols.length < 2) return null;
  const hiddenCount = cols.filter((c) => !isVisible(c)).length;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} title="Choose which line item columns to show"
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 border rounded ${hiddenCount ? 'border-indigo-300 text-indigo-600 bg-indigo-50' : 'border-gray-300 text-gray-500 hover:bg-gray-100'}`}>
        <Eye className="h-3 w-3" /> Line item columns{hiddenCount > 0 && <span className="ml-0.5 text-[10px]">({hiddenCount} hidden)</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 text-left px-3 py-2">
          {cols.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-xs text-gray-700 py-0.5 cursor-pointer">
              <input type="checkbox" checked={isVisible(c)} onChange={(e) => onToggle(c, e.target.checked)} />
              <span className="truncate" title={c.label}>{c.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LineItemTable({
  rows, attrCols, evidenceMap = [], sampleFields = [], hiddenFieldIds = [], onHideSampleField, lookupField, runId, onRowClick, selectedRef,
  cellMeta, onResultClick, openCell, fieldSources = {}, currency = null,
}) {
  const model = useMemo(() => buildLineItemModel(rows, attrCols, evidenceMap), [rows, attrCols, evidenceMap]);
  const cols = useColumnState(runId);
  const [viewerIdx, setViewerIdx] = useState(null);
  const [limit, setLimit] = useState(PAGE_SIZE);   // rows rendered; a long statement is shown in pages
  // A sample with no line items can still be viewed when its attributes cite a place in the evidence.
  const annotatable = useMemo(
    () => model.lines.filter((l) => l.item || buildCitationPanels(l.results, l.fileRefs).panels.length > 0),
    [model.lines],
  );
  const viewIndex = useMemo(() => new Map(annotatable.map((l, i) => [l, i])), [annotatable]);

  // Sample columns: the plan's fields, minus the ones that summarise the line items when each line says it itself.
  const visibleSample = sampleFields.filter((f) => isSampleColumn(model, f) && !hiddenFieldIds.includes(f.id));
  const visibleLine = model.lineCols.filter(cols.isVisible);

  if (!model.lines.length) {
    return <div className="px-5 py-8 text-center text-sm text-gray-400 italic">No samples found for this run.</div>;
  }

  const sampleSpan = 2 + visibleSample.length;     // # + sample + plan fields
  return (
    <div>
      {model.lineCols.length > 1 && (
        <div className="flex items-center justify-end px-4 py-1.5 border-b border-gray-100">
          <ColumnsMenu cols={model.lineCols} isVisible={cols.isVisible} onToggle={(c, on) => cols.set(c.id, on)} />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="line-item-table">
          <thead>
            <tr>
              <th colSpan={sampleSpan} className="px-2 py-1 text-[10px] font-medium bg-indigo-50 text-indigo-700 text-left">Sample</th>
              {model.hasItems && (
                <th colSpan={Math.max(visibleLine.length, 1)} className="px-2 py-1 text-[10px] font-medium bg-slate-100 text-slate-700 text-left border-l border-gray-300">Line item</th>
              )}
              <th className="px-2 py-1 text-[10px] font-medium bg-green-50 text-green-700 text-left border-l border-gray-300">Evidence mapped to this sample</th>
              <th colSpan={2} className="px-2 py-1 text-[10px] font-medium bg-slate-100 text-slate-700 text-left border-l border-gray-300">Outcome</th>
            </tr>
            <tr>
              <th className={`${th} w-7 text-center`}>#</th>
              <th className={`${th} min-w-[80px]`}>Sample</th>
              {visibleSample.map((f) => (
                <th key={f.id} className={`${th} min-w-[90px] group relative`} title={f.label}>
                  {f._liveLabel || f.label}
                  {sourceText(fieldSources, f) && (
                    <span title={sourceText(fieldSources, f)} aria-label={sourceText(fieldSources, f)} role="img" tabIndex={0}
                      className="inline-flex align-middle ml-1 text-gray-400 hover:text-indigo-600 focus:text-indigo-600 normal-case focus:outline-none focus:ring-1 focus:ring-indigo-300 rounded">
                      <Info className="h-3 w-3" />
                    </span>
                  )}
                  {onHideSampleField && (
                    <button type="button" onClick={() => onHideSampleField(f.id)} title="Hide column" aria-label={`Hide ${f.label}`}
                      className="absolute top-0.5 right-0.5 hidden group-hover:flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-red-100 hover:text-red-500">
                      <XIcon className="h-2 w-2" />
                    </button>
                  )}
                </th>
              ))}
              {visibleLine.map((c, i) => <th key={c.id} className={`${th} ${i === 0 ? 'border-l border-gray-300 min-w-[200px]' : ''}`}>{c.label}</th>)}
              <th className={`${th} border-l border-gray-300 min-w-[240px]`}>File · found at · value found</th>
              <th className={`${th} border-l border-gray-300 min-w-[140px]`}>Result by attribute</th>
              <th className={`${th} w-12 text-center`}>View</th>
            </tr>
          </thead>
          <tbody>
            {model.lines.slice(0, limit).map((ln, idx) => {
              const { item } = ln;
              const failed = ln.results.some((r) => r.status === 'failed');
              const topBorder = ln.first && ln.sampleIndex > 0 ? 'border-t-2 border-t-gray-200' : '';
              const note = (ln.results.find((r) => r.status === 'failed') || ln.results[0])?.item?.observation
                || (ln.results.find((r) => r.status === 'failed') || ln.results[0])?.cell?.observation;
              const evidence = evidenceEntries(ln, currency);
              return (
                <tr key={`${ln.sampleRef}-${idx}`}
                  onClick={onRowClick ? () => onRowClick(ln.sampleRef) : undefined}
                  className={`${onRowClick ? 'cursor-pointer hover:bg-blue-50/40' : ''} ${ln.sampleRef === selectedRef ? 'bg-indigo-50' : ''} ${failed ? 'bg-red-50/30' : ''} ${topBorder}`}>
                  <td className={`${td} text-center text-gray-400`}>{idx + 1}</td>
                  <td className={`${td} font-mono font-semibold text-gray-800 whitespace-nowrap`}>{ln.sampleRef}</td>
                  {visibleSample.map((f) => {
                    // a field about one transaction, in a sample with several line items: this line's own value, not the sample's single one
                    const own = ln.siblings > 1 ? lineLevelFieldValue(f, ln.item) : null;
                    const v = own !== null ? own : lookupField(ln.data._fieldData || {}, f);
                    return <td key={f.id} className={`${td} max-w-[130px]`}>{v ? <span className="block truncate" title={v}>{v}</span> : <span className="text-gray-300">—</span>}</td>;
                  })}
                  {model.hasItems && (item ? visibleLine.map((c, i) => {
                    const v = lineCellValue(c, item, currency);
                    return (
                      <td key={c.id} className={`${td} ${i === 0 ? 'border-l border-gray-200 font-mono break-words max-w-[260px]' : ''}`}>
                        {v || <span className="text-gray-300">—</span>}
                        {c.kind === 'ref' && item.source?.location?.file && (
                          <span className="block font-sans text-[10px] text-gray-400">
                            {item.source.location.file}{item.source.location.page_number ? ` p${item.source.location.page_number}` : ''}{item.source.location.row_number && !item.source.location.page_number ? ` row ${item.source.location.row_number}` : ''}
                          </span>
                        )}
                      </td>
                    );
                  }) : (
                    <td colSpan={Math.max(visibleLine.length, 1)} className={`${td} border-l border-gray-200 text-gray-400 italic`}>No line items for this sample</td>
                  ))}
                  <td className={`${td} border-l border-gray-200`}>
                    {evidence.length === 0 && <span className="text-gray-300">—</span>}
                    {evidence.map((e) => (
                      <div key={e.file} className="py-0.5 border-b border-dashed border-gray-100 last:border-b-0">
                        <span className="block text-gray-700 break-words" title={e.file}>{e.file}</span>
                        <span className={`block text-[10px] ${TONE[e.tone] || ''}`}>{e.text}</span>
                        {e.sub && <span className="block text-[10px] text-gray-400">{e.sub}</span>}
                      </div>
                    ))}
                  </td>
                  <td className={`${td} border-l border-gray-200`}>
                    <div className="flex flex-wrap gap-1">
                      {ln.results.map((r) => {
                        const meta = cellMeta ? cellMeta(r.cell) : { canOverride: false, reviewerLabel: null };
                        const isOpen = openCell?.rowRef === ln.sampleRef && openCell?.attrRef === r.attr.ref;
                        const tone = CHIP[r.status] || 'bg-gray-100 text-gray-500';
                        const label = `${r.attr.ref} ${MARK[r.status] || '–'}`;
                        const title = `${r.attr.label || r.attr.ref}: ${STATUS_TEXT[r.status] || r.status || 'no result'}${meta.reviewerLabel ? ` (reviewer: ${meta.reviewerLabel})` : ''}${meta.canOverride ? ' — click to override' : ''}`;
                        return meta.canOverride && onResultClick ? (
                          <button key={r.attr.ref} type="button" title={title} aria-label={title}
                            onClick={(e) => onResultClick(e, ln.sampleRef, r.attr.ref, r.cell)}
                            className={`inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded ${tone} ${isOpen ? 'ring-2 ring-indigo-300' : ''} ${meta.reviewerLabel ? 'underline decoration-indigo-400' : ''}`}>
                            {label}{!isOpen && <PenLine className="h-2 w-2 opacity-40" />}
                          </button>
                        ) : (
                          <span key={r.attr.ref} title={title} className={`inline-flex items-center text-[11px] px-1.5 py-0.5 rounded ${tone}`}>{label}</span>
                        );
                      })}
                    </div>
                    {ln.results.some((r) => cellMeta?.(r.cell)?.reviewerLabel) && <span className="block text-[10px] text-indigo-600 mt-0.5">Reviewer override applied</span>}
                    {note && <span className="block text-[10px] text-gray-500 leading-snug max-w-[240px] line-clamp-2 mt-0.5" title={note}>{note}</span>}
                  </td>
                  <td className={`${td} text-center`}>
                    {viewIndex.has(ln) && (
                      <button type="button" title="View annotated evidence" aria-label="View annotated evidence"
                        onClick={(e) => { e.stopPropagation(); setViewerIdx(viewIndex.get(ln)); }}
                        className="inline-flex items-center justify-center h-7 w-7 rounded border border-gray-300 text-indigo-600 hover:bg-indigo-50">
                        <Highlighter className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {model.lines.length > limit && (
          <button type="button" onClick={() => setLimit((l) => l + PAGE_SIZE)}
            className="w-full py-2 text-xs text-indigo-600 bg-gray-50 hover:bg-gray-100 border-t border-gray-200">
            Show {Math.min(PAGE_SIZE, model.lines.length - limit)} more ({model.lines.length - limit} remaining)
          </button>
        )}
      </div>
      {viewerIdx != null && viewerIdx >= 0 && (
        <LineItemAnnotationViewer lines={annotatable} index={viewerIdx} onIndex={setViewerIdx} runId={runId} onClose={() => setViewerIdx(null)} />
      )}
    </div>
  );
}
